/**
 * StatusBadge Component
 * Traffic light indicator with text label (never color alone)
 * Supports healthy/warning/critical/info states
 */

import type { ReactNode } from 'react';

type BadgeStatus = 'healthy' | 'warning' | 'critical' | 'info' | 'neutral';

interface StatusBadgeProps {
  status: BadgeStatus;
  label?: string;
  size?: 'sm' | 'md';
}

const STATUS_CONFIG: Record<BadgeStatus, { color: string; defaultLabel: string }> = {
  healthy: { color: 'var(--success)', defaultLabel: 'healthy' },
  warning: { color: 'var(--warning)', defaultLabel: 'warning' },
  critical: { color: 'var(--error)', defaultLabel: 'critical' },
  info: { color: 'var(--info)', defaultLabel: 'info' },
  neutral: { color: 'var(--gray-400)', defaultLabel: 'neutral' },
};

export function StatusBadge({ status, label, size = 'md' }: StatusBadgeProps): ReactNode {
  const config = STATUS_CONFIG[status];
  const displayLabel = label ?? config.defaultLabel;
  const dotSize = size === 'sm' ? '6px' : '8px';
  const fontSize = size === 'sm' ? '0.75rem' : '0.8125rem';

  return (
    <span
      className="status-badge"
      role="status"
      aria-label={`Status: ${displayLabel}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        fontSize,
        fontWeight: 500,
        color: 'var(--gray-700)',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: dotSize,
          height: dotSize,
          borderRadius: '50%',
          backgroundColor: config.color,
          flexShrink: 0,
        }}
      />
      {displayLabel}
    </span>
  );
}
